// frontend/src/pages/TradeHistoryPage.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { getTradeHistory } from '../services/api';

function TradeHistoryPage() {
    const [trades, setTrades] = useState([]);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalTrades: 0,
        limitPerPage: 10,
    });
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');

    const fetchHistory = useCallback(async (page = 1) => {
        setIsLoading(true);
        setErrorMessage('');
        try {
            const response = await getTradeHistory(page, pagination.limitPerPage);
            setTrades(response.data.trades);
            setPagination(response.data.pagination);
        } catch (error) {
            console.error("Error fetching trade history:", error);
            setErrorMessage(error.response?.data?.error || 'Could not fetch trade history.');
            setTrades([]); // Clear old data on error
        } finally {
            setIsLoading(false);
        }
    }, [pagination.limitPerPage]); // Dependency on limitPerPage if you allow changing it

    useEffect(() => {
        fetchHistory(pagination.currentPage);
    }, [fetchHistory, pagination.currentPage]); // Fetch when page changes or fetchHistory changes

    const handlePrevPage = () => {
        if (pagination.currentPage > 1) {
            fetchHistory(pagination.currentPage - 1);
        }
    };

    const handleNextPage = () => {
        if (pagination.currentPage < pagination.totalPages) {
            fetchHistory(pagination.currentPage + 1);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString();
    };

    return (
        <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
            <h2>Trade History</h2>
            {errorMessage && <p style={{ color: 'red' }}><strong>Error: {errorMessage}</strong></p>}
            {isLoading && <p>Loading trade history...</p>}

            {!isLoading && trades.length === 0 && !errorMessage && <p>No trades found.</p>}

            {!isLoading && trades.length > 0 && (
                <>
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid #ccc', textAlign: 'left' }}>
                                <th>ID</th>
                                <th>Symbol</th>
                                <th>Type</th>
                                <th>Entry Time</th>
                                <th>Entry Price</th>
                                <th>Exit Time</th>
                                <th>Exit Price</th>
                                <th>Qty</th>
                                <th>P&L</th>
                                <th>Status</th>
                                <th>Exit Reason</th>
                            </tr>
                        </thead>
                        <tbody>
                            {trades.map(trade => (
                                <tr key={trade.id} style={{ borderBottom: '1px solid #eee' }}>
                                    <td>{trade.id}</td>
                                    <td>{trade.option_symbol}</td>
                                    <td>{trade.option_type}</td>
                                    <td>{formatDate(trade.trade_opened_at)}</td>
                                    <td>{trade.entry_price_per_option?.toFixed(2) ?? 'N/A'}</td>
                                    <td>{formatDate(trade.trade_closed_at)}</td>
                                    <td>{trade.exit_price_per_option?.toFixed(2) ?? 'N/A'}</td>
                                    <td>{trade.quantity}</td>
                                    <td style={{ color: trade.pnl >= 0 ? 'green' : 'red' }}>
                                        {trade.pnl?.toFixed(2) ?? 'N/A'}
                                    </td>
                                    <td>{trade.trade_status}</td>
                                    <td>{trade.exit_reason_notes || 'N/A'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <button onClick={handlePrevPage} disabled={pagination.currentPage <= 1 || isLoading}>
                            Previous
                        </button>
                        <span>
                            Page {pagination.currentPage} of {pagination.totalPages} (Total: {pagination.totalTrades} trades)
                        </span>
                        <button onClick={handleNextPage} disabled={pagination.currentPage >= pagination.totalPages || isLoading}>
                            Next
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}

export default TradeHistoryPage;